<template>
    <div class="auth-domain-container" data-qa="auth-domain-container">
        <DsModal
            :is-open="showCreateRecords"
            size="fullscreen"
            show-back
            @back="closeCreateModal"
        >
            <CreateEmailAuthRecordsModal
                v-if="showCreateRecords"
                :is-dns-record-copy-records-view-enabled="isDnsRecordCopyRecordsViewEnabled"
                class="create-email-auth-records-modal"
                data-qa="create-email-auth-records-modal"
                :app-id="appId"
                :domains="domains"
                :domain="createRecordsModalData.domain"
                :provider="createRecordsModalData.provider"
                :dmarc="createRecordsModalData.dmarc"
                :environment="environment"
                @close="closeAll"
                @refresh-email-auth="refreshEmailAuth"
            />
        </DsModal>
        <div :class="formClass">
            <div class="skinny-form">
                <h1 class="secondary-title" data-qa="secondary-title">
                    {{ title }}
                </h1>

                <section class="box">
                    <p>{{ description }}</p>

                    <DsInputField
                        v-model="selectedDomainName"
                        class="domain-name-input"
                        name="domain"
                        data-qa="domain-name-input"
                        :label="domainLabel"
                        :readonly="!isNewDomain"
                        :required="isNewDomain"
                        :submitted="domainSubmitted"
                        :invalid="invalidNewDomain"
                        @blur="handleDomainBlur"
                    >
                        <template #error>
                            {{ domainError }}
                        </template>
                    </DsInputField>

                    <p>
                        {{ $t('email.auth.authModal.emailRegistrarDescription') }}
                    </p>

                    <DsSelectField
                        v-model="selectedProvider"
                        class="provider-dropdown"
                        data-qa="provider-dropdown"
                        name="providers"
                        :label="$t('email.auth.authModal.provider')"
                        label-prop="name"
                        value-prop="id"
                        bind-value-only
                        required
                        :options="providers"
                    />
                </section>
            </div>

            <Transition name="fadeInOut" mode="out-in" appear>
                <div
                    v-show="showDNSHelp"
                    v-if="isDnsRecordCopyRecordsViewEnabled"
                    class="skinny-form host-help-container"
                >
                    <!-- eslint-disable vue/no-v-html -->
                    <p class="host-help-text" v-html="hostHelpText" />
                    <!-- eslint-enable vue/no-v-html -->

                    <div v-if="foundSelectedProvider">
                        <DsOutlineButton
                            as="a"
                            :href="foundSelectedProvider.url"
                            target="_blank"
                            rel="noopener noreferrer"
                            trailing-icon="external-link"
                        >
                            {{ $t('email.auth.authModal.hostHelp.cta', { dnsHost: foundSelectedProvider.name || '' }) }}
                        </DsOutlineButton>
                    </div>
                </div>
            </Transition>

            <DmarcSettings
                ref="dmarcSettings"
                :submitted="isSubmitted"
                :is-add-validation-to-email-domain-verification-enabled="isAddValidationToEmailDomainVerificationEnabled"
                data-qa="dmarc-settings"
                @update-dmarc="handleUpdateDmarc"
            />

            <div class="actions">
                <DsFilledButton
                    :disabled="shouldDisableButton"
                    data-qa="continue-button"
                    @click="next"
                >
                    {{ $t('global.continue') }}
                </DsFilledButton>

                <!-- eslint-disable vue/no-v-html -->
                <p v-html="$t('global.learnDkim', { learnMore: learnMore })" />
                <!-- eslint-enable vue/no-v-html -->
            </div>
        </div>
    </div>
</template>

<script>
import uuid from 'uuid/v4';
import CreateEmailAuthRecordsModal from '@/components/settings/email/CreateEmailAuthRecordsModal';
import DmarcSettings from '@/components/settings/email/DmarcSettings';
import { filterDomains } from '@/components/settings/util/domains.util';
import { parseDomain, fromUrl, ParseResultType } from 'parse-domain';

import {
    DKIM_PROVIDER_GODADDY_ID,
    DKIM_PROVIDER_BLUEHOST_ID,
    DKIM_PROVIDER_HOSTGATOR_ID,
    DKIM_PROVIDER_DREAMHOST_ID,
    DKIM_PROVIDER_LIQUIDWEB_ID,
    DKIM_PROVIDER_INMOTION_ID,
    DKIM_PROVIDER_OTHER_ID,
} from '@/components/settings/settings.constants';

export const CUSTOM_DOMAIN_REGEX = '\\b(?:[a-zA-Z0-9](?:[a-zA-Z0-9\\-]{0,61}[a-zA-Z0-9])?\\.){2}(?:[a-zA-Z]{2,63}(?:\\.[a-zA-Z]{2,63})*)\\b';
export const EMAIL_DOMAIN_REGEX = '\\b(?:[a-zA-Z0-9](?:[a-zA-Z0-9\\-]{0,61}[a-zA-Z0-9])?\\.){1}(?:[a-zA-Z]{2,63}(?:\\.[a-zA-Z]{2,63})*)\\b';
export const HTTP_PREFIX = 'http://';
export const HTTPS_PREFIX = 'https://';
export const WWW_PREFIX = 'www.';

const POLICY_NONE = 'none';
const POLICY_QUARANTINE = 'quarantine';
const POLICY_REJECT = 'reject';
const DMARC_PCT = 5;

export default {
    components: {
        CreateEmailAuthRecordsModal,
        DmarcSettings,
    },

    emits: ['close', 'retry'],

    props: {
        appId: {
            type: String,
            default: '',
        },
        domains: {
            type: Array,
            default: () => [],
        },
        domain: {
            type: Object,
            default: () => {},
        },
        environment: {
            type: String,
            default: '',
        },
        isNewDomain: {
            type: Boolean,
            default: false,
        },
        isDnsRecordCopyRecordsViewEnabled: {
            type: Boolean,
            default: false,
        },
        isAddValidationToEmailDomainVerificationEnabled: {
            type: Boolean,
            default: false,
        },
    },

    data() {
        return {
            selectedDomain: {},
            selectedDomainName: null,
            sanitizedDomainName: null,
            selectedProvider: null,
            providers: [
                { id: DKIM_PROVIDER_GODADDY_ID, name: 'GoDaddy', url: 'https://sso.godaddy.com/', helpUrl: 'https://www.godaddy.com/help/add-a-cname-record-19236' },
                { id: DKIM_PROVIDER_BLUEHOST_ID, name: 'BlueHost', url: 'https://www.bluehost.com/my-account/login', helpUrl: 'https://my.bluehost.com/hosting/help/559/#add' },
                { id: DKIM_PROVIDER_HOSTGATOR_ID, name: 'Host Gator', url: 'https://www.hostgator.com/my-account/login', helpUrl: 'https://support.hostgator.com/articles/articles/hosting-guide/lets-get-started/dns-name-servers/manage-dns-records-with-hostgatorenom' },
                { id: DKIM_PROVIDER_DREAMHOST_ID, name: 'DreamHost', url: 'https://panel.dreamhost.com/', helpUrl: 'https://help.dreamhost.com/hc/en-us/articles/215414867-How-do-I-add-custom-DNS-records-/#CNAME_record' },
                { id: DKIM_PROVIDER_LIQUIDWEB_ID, name: 'Liquid Web', url: 'https://login.liquidweb.com', helpUrl: 'https://www.liquidweb.com/kb/how-to-add-or-modify-dns-records-in-manage/#addRecord' },
                { id: DKIM_PROVIDER_INMOTION_ID, name: 'In-Motion', url: 'https://secure1.inmotionhosting.com/index/login', helpUrl: 'https://www.inmotionhosting.com/support/domain-names/dns-nameserver-changes/create-cname-record' },
                { id: DKIM_PROVIDER_OTHER_ID, name: 'Other', url: 'https://help.infusionsoft.com/help/dkim', helpUrl: 'https://help.infusionsoft.com/help/dkim' },
            ],
            learnDkimUrl: 'https://help.keap.com/help/dmarc',
            isSubmitted: false,
            existingDmarc: true,
            policy: POLICY_QUARANTINE,
            pct: DMARC_PCT,
            rua: '',
            showCreateRecords: false,
            createRecordsModalData: {},
            domainType: null,
            formClass: 'form',
        };
    },
    created() {
        this.POLICY_NONE = POLICY_NONE;

        const POLICY_OPTIONS = [
            {
                label: this.$t('email.auth.authModal.dmarc.policy.none'),
                value: POLICY_NONE,
            },
            {
                label: this.$t('email.auth.authModal.dmarc.policy.quarantine'),
                value: POLICY_QUARANTINE,
            },
            {
                label: this.$t('email.auth.authModal.dmarc.policy.reject'),
                value: POLICY_REJECT,
            },
        ];

        this.POLICY_OPTIONS = POLICY_OPTIONS;

        if (this.domain?.domain) {
            this.selectedDomainName = this.domain.domain;
            this.selectedDomain = this.domain;
        }
    },

    watch: {
        selectedDomainName: {
            handler(newValue) {
                let result = newValue.toLowerCase();

                // Any white space replaced by -:
                result = result.replace(/\s/, '-');
                // No more than one '-' sequence:
                result = result.replace(/[-]{2,}/, '-');
                // No more than one ':' sequence:
                result = result.replace(/[:]{2,}/, ':');
                // No more than one '.' sequence:
                result = result.replace(/[.]{2,}/, '.');
                // No more than two '/' sequence:
                result = result.replace(/[/]{3,}/, '//');
                // Only words or '.' ':' '/' '-'
                result = result.replace(/[^\w:/.-]/gi, '');

                this.$nextTick(() => {
                    this.selectedDomainName = result;
                    this.isSubmitted = false;
                });
            },
        },
    },

    computed: {
        title() {
            return this.$t('email.auth.authModal.titleEmail');
        },

        description() {
            return this.$t('email.auth.authModal.descriptionEmail');
        },

        domainLabel() {
            return this.$t('email.auth.authModal.email');
        },

        learnMore() {
            return `<a href="${this.learnDkimUrl}" target="_blank" rel="noopener noreferrer">${this.$t('global.learnMore')}</a>`;
        },

        domainError() {
            if (this.isFreeDomain) {
                return this.$t('email.auth.authModal.freeDomainError');
            }

            switch (this.domainType) {
            case ParseResultType.Ip:
                return this.$t('email.auth.authModal.ipError');
            case ParseResultType.Invalid:
                return this.$t('email.auth.authModal.unsupportedDomainCharacterError');
            case ParseResultType.Reserved:
                return this.$t('email.auth.authModal.reservedDomainError');
            default:
                return this.$t('email.auth.authModal.emailDomainError');
            }
        },

        domainSubmitted() {
            return (this.isSubmitted && this.isNewDomain) || this.invalidNewDomain;
        },

        showPolicySubOptions() {
            return this.policy === POLICY_QUARANTINE || this.policy === POLICY_REJECT;
        },

        isFreeDomain() {
            if (this.selectedDomainName && filterDomains([{ domain: this.selectedDomainName }]).length === 0) {
                return true;
            }

            return false;
        },

        invalidNewDomain() {
            if (this.isFreeDomain) {
                return true;
            }

            if (this.sanitizedDomainName !== null && !this.sanitizedDomainName) {
                return true;
            }

            return false;
        },

        unsureUrl() {
            let domainToCheck;

            if (this.selectedDomain.domain) {
                domainToCheck = this.selectedDomain.domain;
            } else if (this.sanitizedDomainName) {
                domainToCheck = this.sanitizedDomainName;
            }

            return domainToCheck ? `https://dmarcian.com/dmarc-inspector/?domain=${domainToCheck}#dm-inspect-dmarc` : 'https://dmarcian.com/dmarc-inspector/#dm-inspect-dmarc';
        },

        shouldDisableButton() {
            if (this.isAddValidationToEmailDomainVerificationEnabled) {
                const isMissingNewDomain = this.isNewDomain && !this.sanitizedDomainName;
                const isInvalidRuaWhenRequired = !this.existingDmarc && this.showPolicySubOptions && ((this.$refs.dmarcSettings && !this.$refs.dmarcSettings.isRuaValid) || !this.rua);

                return isMissingNewDomain || !this.selectedProvider || this.invalidNewDomain || isInvalidRuaWhenRequired;
            }

            return (this.isNewDomain && !this.sanitizedDomainName) || !this.selectedProvider || this.invalidNewDomain;
        },

        showDNSHelp() {
            return this.selectedProvider && this.selectedProvider !== DKIM_PROVIDER_OTHER_ID;
        },

        foundSelectedProvider() {
            return this.providers.find((provider) => provider.id === this.selectedProvider);
        },

        hostHelpText() {
            let domain = '';

            // This is to ensure that the help text is displayed correctly if no domain is set before the user selects a provider.
            if (this.selectedDomainName) {
                domain = ` ${this.selectedDomainName}`;
            }

            return this.$t('email.auth.authModal.hostHelp.helpText', {
                dnsHost: this.foundSelectedProvider?.name || '',
                domain,
            });
        },
    },

    methods: {
        closeAll() {
            this.closeCreateModal();
            this.$emit('close');
        },

        refreshEmailAuth() {
            this.$emit('retry');
        },

        closeCreateModal() {
            this.showCreateRecords = false;
        },

        handleDomainBlur() {
            if (this.isNewDomain) {
                this.sanitizedDomainName = this.sanitizeDomain(this.selectedDomainName);

                if (this.sanitizedDomainName) {
                    this.selectedDomainName = this.sanitizedDomainName;
                }
            }
        },

        handleUpdateDmarc(dmarc) {
            this.existingDmarc = !dmarc.createOrUpdateDmarc;
            this.policy = dmarc.policy;
            this.pct = dmarc.pct;
            this.rua = dmarc.rua;
        },

        next() {
            this.isSubmitted = true;

            if (this.isNewDomain) {
                if (!this.sanitizedDomainName) {
                    return;
                }

                this.selectedDomain.dkimName = this.sanitizedDomainName;
                this.selectedDomain.domain = this.sanitizedDomainName;
                this.selectedDomain.domainId = uuid();
            }

            const data = {
                domain: this.selectedDomain,
                provider: this.foundSelectedProvider,
                isNewDomain: this.isNewDomain,
            };

            if (!this.existingDmarc) {
                data.dmarc = {
                    policy: this.policy,
                    pct: this.policy === POLICY_NONE ? null : this.pct,
                    rua: this.policy === POLICY_NONE ? '' : this.rua,
                };
            }

            this.createRecordsModalData = data;
            this.showCreateRecords = true;
        },

        removeUriPrefix(domain) {
            domain = (domain.startsWith(HTTPS_PREFIX)) ? domain.replace(HTTPS_PREFIX, '') : domain;
            domain = (domain.startsWith(HTTP_PREFIX)) ? domain.replace(HTTP_PREFIX, '') : domain;
            domain = (domain.startsWith(WWW_PREFIX)) ? domain.replace(WWW_PREFIX, '') : domain;

            return domain;
        },

        sanitizeDomain(domain) {
            const {
                labels, type,
            } = parseDomain(fromUrl(domain));

            switch (type) {
            case ParseResultType.Invalid:
            case ParseResultType.Ip:
            case ParseResultType.Reserved:
                this.domainType = type;

                return '';

            case ParseResultType.NotListed:
                if (this.isAddValidationToEmailDomainVerificationEnabled) {
                    this.domainType = type;

                    return '';
                }
                // falls through
            default:
                this.domainType = type;

                return labels.join('.');
            }
        },

        openDmarcChecker() {
            window.open(this.unsureUrl, '_blank', 'noopener', 'noreferrer');
        },
    },
};

</script>

<style lang="scss" rel="stylesheet/scss" type="scss" scoped>
.auth-domain-container {
    display: flex;
    flex-direction: column;
    align-items: center;
}

.create-email-auth-records-modal ::v-deep .actions {
    .confirm-button, .close-button {
        background-color: $color-blue-700;
        color: $color-white;
    }
}

.domain-name-input ::v-deep input {
    color: $color-text-normal;
}
.provider-dropdown ::v-deep {
    .select-container label {
        padding-top: 0;
    }
    input {
        color: $color-text-normal;
    }
}

.form {
    max-width: 62.50rem;
    width:100%;
}

.skinny-form {
    max-width: 31.25rem;
}

.dmarc-gen-selector {
    display: flex;
    flex-direction: row;
    justify-content: space-between;

    a {
        margin: $spacing-200;
    }
}

.dmarc-check-link {
    margin: $spacing-200;
}

.actions {
    display: flex;
    flex-direction: column;
    margin-top: $spacing-300;

    button {
        width: fit-content;
        margin-bottom: $spacing-300;
        color: $color-white;
    }
}


.policy-toggle ::v-deep {
    margin-bottom: $spacing-200;
    .button-toggle-option {
        color: $color-blue;
    }
    .button-toggle-option.selected {
        color: $color-white;
    }
}

.policy-description-header {
    display: flex;

    h3 {
        margin-right: $spacing-100;
    }
}

.host-help-container {
    margin-bottom: $spacing-300;
    display: flex;
    flex-direction: column;
    gap: $spacing-300;
}

.host-help-text {
    margin-bottom: 0;
}

// transition: fadeInOut
.fadeInOut-enter-active {
    opacity: 1.0;
    transition: opacity .25s ease-out;
}

.fadeInOut-enter, .fadeInOut-enter-from {
    opacity: 0;
    transition: opacity .25s ease-out;
    transition-delay: .15s;
}

@media($small) {
    .box {
        width: 100%;
    }

    .actions {
        width: 100%;
    }
}
</style>
